import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import ApprovalOutlined from '@mui/icons-material/ApprovalOutlined';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import FolderCopyOutlined from '@mui/icons-material/FolderCopyOutlined';
import RemoveRedEyeOutlined from '@mui/icons-material/RemoveRedEyeOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import UpdateOutlined from '@mui/icons-material/UpdateOutlined';
import SvgIcoChatBubble from 'components/icon/icons/IcoChatBubble';

import type dayjs from 'dayjs';
import type { NotificationLastKey } from 'services/notifications/types';

export enum Event {
  EDITED = 'edited',
  NOT_VIEWED = 'not_viewed',
  VIEWED = 'viewed',
  SHARED = 'shared',
  COMMENTED = 'commented',
  DOWNLOADED = 'downloaded',
  UPDATED = 'updated',
  REQUESTED = 'requested',
  APPROVED = 'approved',
  DELETED = 'deleted',
  RESTORED = 'restored',
  MOVED = 'moved',
  SIGNED = 'signed',
  VOIDED = 'voided',
  WON = 'won',
  LOST = 'lost',
  AUTOMATED = 'automated',
  EXPIRED = 'expired',
  CREATED_FROM_TEMPLATE = 'created_from_template',
  DUPLICATE = 'duplicate',
  CREATED = 'created',
}

export type EventKey = `${Event}`;

export const eventMapping: Record<EventKey, { key: string; icon: React.ElementType }> = {
  [Event.EDITED]: {
    key: 'edited_event',
    icon: EditOutlined,
  },
  [Event.NOT_VIEWED]: {
    key: 'not_viewed_event',
    icon: RemoveRedEyeOutlined,
  },
  [Event.VIEWED]: {
    key: 'viewed_event',
    icon: RemoveRedEyeOutlined,
  },
  [Event.SHARED]: {
    key: 'shared_event',
    icon: SendOutlined,
  },
  [Event.COMMENTED]: {
    key: 'commented_event',
    icon: SvgIcoChatBubble,
  },
  [Event.DOWNLOADED]: {
    key: 'dowloaded_event',
    icon: DownloadOutlined,
  },
  [Event.UPDATED]: {
    key: 'updated_event',
    icon: UpdateOutlined,
  },
  [Event.REQUESTED]: {
    key: 'requested_event',
    icon: SendOutlined,
  },
  [Event.APPROVED]: {
    key: 'approved_event',
    icon: ApprovalOutlined,
  },
  [Event.DELETED]: {
    key: 'deleted_event',
    icon: DeleteOutlined,
  },
  [Event.RESTORED]: {
    key: 'restored_event',
    icon: CopyAllOutlined,
  },
  [Event.MOVED]: {
    key: 'moved_event',
    icon: FolderCopyOutlined,
  },
  [Event.SIGNED]: {
    key: 'signed_event',
    icon: DriveFileRenameOutline,
  },
  [Event.VOIDED]: {
    key: 'voided_event',
    icon: DriveFileRenameOutline,
  },
  [Event.WON]: {
    key: 'won_event',
    icon: CheckCircleOutline,
  },
  [Event.LOST]: {
    key: 'lost_event',
    icon: CancelOutlined,
  },
  [Event.AUTOMATED]: {
    key: 'automated_event',
    icon: SmartToyOutlined,
  },
  [Event.EXPIRED]: {
    key: 'expired_event',
    icon: SmartToyOutlined,
  },
  [Event.CREATED_FROM_TEMPLATE]: {
    key: 'created_from_template_event',
    icon: CopyAllOutlined,
  },
  [Event.DUPLICATE]: {
    key: 'duplicate_event',
    icon: CopyAllOutlined,
  },
  [Event.CREATED]: {
    key: 'created_event',
    icon: AddCircleOutline,
  },
};

export type EventType = {
  edited: { author: string; documentName: string };
  not_viewed: { documentName: string; viewedTime: string };
  viewed: { viewerName: string; companyName: string; documentName: string };
  shared: { author: string; documentName: string; recipientsCount?: number; recipient?: string; collaboratorType?: string };
  commented: { author: string; documentName: string; commentText: string; threadId?: string; previewUrl?: string };
  downloaded: { author: string; documentName: string };
  updated: { author: string; documentName: string; status: string };
  requested: { author: string; documentName: string; approver: string };
  approved: { author: string; documentName: string };
  deleted: { author: string; documentName: string };
  restored: { author: string; documentName: string };
  moved: { author: string; documentName: string; folderName: string };
  signed: { author: string; documentName: string; status?: string };
  voided: { author: string; documentName: string };
  won: { author: string; documentName: string };
  lost: { author: string; documentName: string };
  automated: { documentName: string; reminder: string };
  expired: { documentName: string };
  created_from_template: { author: string; documentName: string; templateName: string };
  duplicate: { author: string; documentName: string; duplicateDocumentName: string };
  created: { author: string; documentName: string };
};

export type EventRow = {
  [K in EventKey]: {
    notificationId: string;
    type: K;
    key: string;
    icon: React.ElementType;
    createdAt: dayjs.Dayjs;
    viewed: boolean;
    documentId: string;
  } & EventType[K];
}[EventKey];

export type BaseNotificationFields = {
  viewed: boolean;
  createdAt: dayjs.Dayjs;
  notificationId: string;
  documentId: string;
};

export type TransformedNotificationItem = {
  [K in EventKey]: BaseNotificationFields & { type: K } & EventType[K];
}[EventKey];

export type TransformedNotificationsFeedResponse = {
  items: TransformedNotificationItem[];
  lastKey: NotificationLastKey;
};

export type CommentedEvent = Extract<TransformedNotificationItem, { type: `${Event.COMMENTED}` }>;

export const isCommentedEvent = (event: TransformedNotificationItem): event is CommentedEvent => event.type === Event.COMMENTED;

export const isViewedEvent = (event: TransformedNotificationItem): boolean =>
  event.type === Event.VIEWED || event.type === Event.NOT_VIEWED;

export function getEventRow<K extends EventKey>(
  eventType: K,
  event: EventType[K] & {
    notificationId: string;
    type: K;
    createdAt: dayjs.Dayjs;
    viewed: boolean;
    documentId: string;
  }
) {
  return {
    ...eventMapping[eventType],
    ...event,
    type: eventType,
  };
}
